/**
 * Backend client for markup-set persistence (replaces the localStorage prototype).
 *
 * The server models a per-user publish lifecycle per (file, page, revision): each user
 * has one private `draft` set they edit, plus everyone's `published` snapshots are
 * visible read-only. Saves carry an optimistic-lock `version`; a stale save returns 409
 * (surfaced here as {@link MarkupVersionConflict}) so the caller can refetch + rebase.
 *
 * Available to every authenticated user. Writes are owner-scoped server-side (each user
 * edits only their own draft); a defensive 403 on load is treated as "no server markup".
 */

import { AxiosError, isAxiosError } from "axios";
import axios from "../config/axios";
import { blobJson } from "../utils/blobText";
import { contentDispositionFileName, responseHeader } from "../utils/responseHeaders";
import { withTransientRetry } from "./transientRetry";
import {
    SetMarkupChanged,
    setMarkupLayer,
    type SetMarkupLayer,
} from "./attachmentSets/markupLayer";
import {
    Calibration,
    MarkupShape,
    savableShapes,
    type MarkupDownloadOptions,
} from "../components/fileviewer/markupmode/markupTypes";
import type { MarkupExportSet } from "../components/fileviewer/markupmode/markupExportSets";
import type { MarkupImageAttachment } from "../components/fileviewer/markupmode/imageAttachments";
import type { SheetTransform } from "../components/overlay/overlayTypes";

/** One sheet of the layered overlay export (`MarkupApi.overlayPdf`). */
export interface OverlayPdfSheet {
    /** The file the viewer renders (its first page); the server reads and authorizes it. */
    fileId: string;
    name: string;
    tint: { r: number; g: number; b: number } | null;
    paperLuminance: number;
    opacity: number;
    visible: boolean;
    /** Canvas contract onto the base sheet; null for the base. */
    transform: SheetTransform | null;
    clip: [number, number, number, number] | null;
}

/** What the user decides about a markup Marvin drew. */
export type MarvinShapesDecision = "keep" | "remove";

/** One markup set as returned by the backend. `shapes`/`calibration` are opaque JSON. */
export interface ApiMarkupSet {
    id: string;
    file_id: string;
    page: number;
    revision_id: string | null;
    /** Exact drawing version, when the server uses UUID-scoped markup persistence. */
    anchor_version_id?: string | null;
    created_by: string;
    status: "draft" | "published";
    shapes: MarkupShape[];
    calibration: Calibration | null;
    version: number;
    published_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    /**
     * True when this set belongs to the requesting user (server-computed: `created_by` is
     * the internal user id, which the client can't match against its Stytch member id).
     * Only populated by `getVisible`; absent/false on save/publish responses.
     */
    is_own?: boolean;
    /** Display name for `created_by` (server-resolved); null if the user row is gone. */
    created_by_name?: string | null;
}

/**
 * Thrown when a draft save loses the optimistic-lock race (HTTP 409). Carries the
 * server's current version so the caller can refetch, rebase its pending edits, and
 * retry with the fresh `expected_version`.
 */
export class MarkupVersionConflict extends Error {
    constructor(public currentVersion: number | null) {
        super("Markup draft version conflict");
        this.name = "MarkupVersionConflict";
    }
}

export interface SaveMarkupBody {
    revision_id: string | null;
    section_group_id?: string;
    /** Declares which optional shape fields this full-snapshot writer understands —
     *  see MARKUP_SCHEMA.md. The server restores, from the stored set, a field newer
     *  than this version that the save leaves out (`_FIELD_INTRO_VERSION` in ai_engine
     *  `db/repositories/markup_repository.py`). Every field there is 1.2.0 or older: the
     *  server's 1.3.0 and 1.4.0 added kinds (the image snapshot; Cloud+ and Note), not
     *  fields of a kind an older writer knew. A writer must not claim a version whose
     *  fields it does not write, so this moves with the first newer field it writes. */
    schema_version: "1.2.0";
    shapes: MarkupShape[];
    calibration: Calibration | null;
    expected_version: number | null;
    delivery?: MarkupDeliveryRequest;
}

/** Additive receipt/generation contract. Legacy servers ignore its absence; callers
 * must not use a legacy delete as a substitute for fenced Clear. */
export interface MarkupDeliveryRequest {
    operation_id: string;
    scope_generation: number;
}

export interface MarkupDeliveryReceipt {
    operation_id: string;
    scope_generation: number;
    confirmed_version: number | null;
    markup_id: string | null;
    payload_sha256: string;
    scope_id?: string;
}

export interface MarkupScopeMetadata {
    capability_version: number;
    fenced_mode: boolean;
    scope_generation: number;
    scope_id?: string;
}

export interface ApiMarkupSaveResult extends ApiMarkupSet {
    delivery?: MarkupDeliveryReceipt;
}

/**
 * How long one markup read or write may take before it fails as a lost connection does. A page's
 * saves, Clears and loads queue behind each other (`persistence/scopeWrites.ts`), so one
 * unanswered request would hold every later one for that page, in every viewer, until reload.
 */
export const MARKUP_REQUEST_TIMEOUT_MS = 30_000;

/** Extract `current_version` from the 409 detail payload (shape: {message, current_version}). */
function currentVersionFrom(detail: unknown): number | null {
    if (detail && typeof detail === "object" && "current_version" in detail) {
        const v = (detail as { current_version: unknown }).current_version;
        return typeof v === "number" ? v : null;
    }
    return null;
}

interface ApiMarkupImage {
    imageRef: string;
    mime: "image/png" | "image/jpeg";
    size: number;
}

/** A spec section's own pages cut from its book, with markups burned in as selected. */
export interface SectionDownload {
    blob: Blob;
    /** The server's `Content-Disposition` name (`{code} - {title}.pdf`). */
    fileName?: string;
    /** Where the pages sit in the book: 1-based, inclusive. */
    pages: { start: number; end: number; sourcePageCount: number };
}

/**
 * Thrown when the server can't tell which pages of the book are the section's (HTTP 409
 * `section_pages_unknown`).
 */
export class SectionPagesUnknown extends Error {
    constructor(message?: string) {
        super(message || "Section pages unknown");
        this.name = "SectionPagesUnknown";
    }
}

function pageHeader(headers: unknown, name: string): number | undefined {
    const value = responseHeader(headers, name)?.trim();
    return value && /^\d+$/.test(value) ? Number(value) : undefined;
}

/**
 * The request asks for a blob, so a refusal's JSON body arrives as one too: read it back,
 * turn the 409 `section_pages_unknown` into {@link SectionPagesUnknown}, and leave any other
 * failure as it was, with its `detail` restored for the log.
 */
async function sectionDownloadError(err: unknown): Promise<unknown> {
    if (!isAxiosError(err) || !err.response) return err;
    const data: unknown = err.response.data;
    const body = data instanceof Blob ? await blobJson(data) : data;
    err.response.data = body;
    const detail = (body as { detail?: { code?: unknown; message?: unknown } } | null)?.detail;
    if (err.response.status === 409 && detail?.code === "section_pages_unknown") {
        return new SectionPagesUnknown(
            typeof detail.message === "string" ? detail.message : undefined,
        );
    }
    return err;
}

/*
 * An attachment set page's markup lives at the set's own routes, not under `/markup`: the viewer's
 * file id carries the page's address, and `setMarkupLayer` (the attachment set client) reads, saves
 * and clears its one layer. Below, each operation hands a set page to it, or says why the
 * operation has no set route.
 */

/** What an error message calls a set page: the screens name the section by the item's kind. */
const setPageWord = "attachment page";

/** The 404 an old server gives `scope-metadata`: the outbox then saves directly (§3.2). */
function noScopeMetadata(): never {
    throw new AxiosError(
        `No scope metadata for an ${setPageWord}`,
        "ERR_BAD_REQUEST",
        undefined,
        undefined,
        {
            status: 404,
            statusText: "Not Found",
            data: null,
            headers: {},
            config: {} as never,
        },
    );
}

/**
 * A set page has no route for image sidecars or burned-in exports (only the layer's GET, PUT and
 * DELETE). Say so instead of sending the set page address to the file
 * routes, where it would 404.
 */
function rejectSetFile(fileId: string, what: string): void {
    if (setMarkupLayer(fileId)) throw new Error(`${what} are not available on an ${setPageWord}.`);
}

/**
 * Clears a set page's layer; a refusal because it changed since this tab read it is a
 * {@link MarkupVersionConflict}, as a stale save's is.
 */
async function clearSetLayer(layer: SetMarkupLayer): Promise<void> {
    try {
        await layer.clear();
    } catch (err) {
        if (err instanceof SetMarkupChanged) throw new MarkupVersionConflict(err.currentVersion);
        throw err;
    }
}

/**
 * The shapes a save of `fileId`'s markup sends (`MarkupApi.saveDraft`): those the server keeps on
 * its surface (`savableShapes`; an image snapshot only on a set page's layer, which stores it). A
 * save that answers without a shape left out here has not deleted it.
 */
export function savedMarkupShapes(fileId: string, shapes: readonly MarkupShape[]): MarkupShape[] {
    return savableShapes(shapes, {
        storesSnapshots: setMarkupLayer(fileId)?.storesSnapshots ?? false,
    });
}

export const MarkupApi = {
    /**
     * Markup sets visible to the caller for this page+revision: their own draft (if any)
     * plus everyone's published snapshots. Returns [] for a 403 or an empty page.
     */
    getVisible: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        sectionGroupId?: string | null,
    ): Promise<ApiMarkupSet[]> => {
        const layer = setMarkupLayer(fileId);
        if (layer) return layer.read();
        try {
            const resp = await axios.get(`/api/ai/markup/${fileId}/${page}`, {
                params: {
                    ...(revisionId ? { revision_id: revisionId } : {}),
                    ...(sectionGroupId ? { section_group_id: sectionGroupId } : {}),
                },
                timeout: MARKUP_REQUEST_TIMEOUT_MS,
            });
            return resp.data;
        } catch (err) {
            // Defensive: treat a 403 as "no server markup" rather than throwing into the
            // viewer (e.g. a permissions misconfiguration must not break sheet viewing).
            if (isAxiosError(err) && err.response?.status === 403) return [];
            throw err;
        }
    },

    /**
     * Upsert the caller's draft. `expectedVersion` is the version the client last saw
     * (null on first save). A stale version rejects with {@link MarkupVersionConflict}.
     */
    saveDraft: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        shapes: MarkupShape[],
        calibration: Calibration | null,
        expectedVersion: number | null,
        delivery?: MarkupDeliveryRequest,
        /** Exact drawing version, trailing-optional like `getVisible`/`purgeHistory`/
         *  `download` so an unversioned caller's argument list is unchanged. */
        sectionGroupId?: string | null,
    ): Promise<ApiMarkupSaveResult> => {
        const layer = setMarkupLayer(fileId);
        const body: SaveMarkupBody = {
            revision_id: revisionId,
            schema_version: "1.2.0",
            // One shape whose `type` is outside the server's discriminated union fails
            // request-body validation and 422s the WHOLE page's draft. Until the server
            // stores every kind this build draws, drop those rather than lose everything
            // else on the page with them; no tool, paste or Tool Chest card puts one on a
            // page saved here (`keepsKind`), so in practice this catches queued outbox
            // entries. The page keeps what was left out, with a save error
            // (`useMarkupMode`). An image snapshot stays only where the layer stores it.
            shapes: savedMarkupShapes(fileId, shapes),
            calibration,
            expected_version: expectedVersion,
            ...(sectionGroupId ? { section_group_id: sectionGroupId } : {}),
            ...(delivery ? { delivery } : {}),
        };
        try {
            if (layer) return await layer.save(body);
            const resp = await axios.put(`/api/ai/markup/${fileId}/${page}`, body, {
                timeout: MARKUP_REQUEST_TIMEOUT_MS,
            });
            return resp.data;
        } catch (err) {
            if (isAxiosError(err) && err.response?.status === 409) {
                throw new MarkupVersionConflict(currentVersionFrom(err.response.data?.detail));
            }
            throw err;
        }
    },

    /**
     * Keep or remove markups Marvin drew in the caller's own draft (`ai_engine/app/marvin_markups.py`).
     * Keep sets `kept` (solid, exported); Remove deletes them with their labels. For a card in the
     * conversation, whose sheet is not necessarily open: on the sheet itself the store's own
     * `keepMarvinMarkup` / `removeMarvinMarkup` act on the live draft instead. Repeating a decision is
     * harmless. Resolves to the saved draft.
     */
    decideMarvinShapes: async (
        fileId: string,
        page: number,
        shapeIds: string[],
        decision: MarvinShapesDecision,
        /** The revision the draft was saved under; unset leaves the server to resolve one. */
        revisionId?: string | null,
    ): Promise<ApiMarkupSet> => {
        const resp = await axios.post<ApiMarkupSet>(
            `/api/ai/markup/${fileId}/${page}/marvin-shapes`,
            {
                decision,
                shape_ids: shapeIds,
                ...(revisionId ? { revision_id: revisionId } : {}),
            },
            { timeout: MARKUP_REQUEST_TIMEOUT_MS },
        );
        return resp.data;
    },

    getScopeMetadata: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        sectionGroupId?: string | null,
    ): Promise<MarkupScopeMetadata> => {
        if (setMarkupLayer(fileId)) return noScopeMetadata();
        const response = await axios.get<MarkupScopeMetadata>(
            `/api/ai/markup/${fileId}/${page}/scope-metadata`,
            {
                params: {
                    ...(revisionId ? { revision_id: revisionId } : {}),
                    ...(sectionGroupId ? { section_group_id: sectionGroupId } : {}),
                },
                timeout: MARKUP_REQUEST_TIMEOUT_MS,
            },
        );
        return response.data;
    },

    /** Durable ordered Clear. Never fall back to remove()+purgeHistory(): an old
     * first-save could arrive after that pair and resurrect the draft. */
    clearScope: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        delivery: MarkupDeliveryRequest,
        sectionGroupId?: string | null,
    ): Promise<MarkupDeliveryReceipt> => {
        if (setMarkupLayer(fileId)) {
            throw new Error(
                `An ${setPageWord} has no fenced Clear; it clears through the legacy path`,
            );
        }
        const response = await axios.post<MarkupDeliveryReceipt>(
            `/api/ai/markup/${fileId}/${page}/clear-scope`,
            {
                revision_id: revisionId,
                delivery,
                ...(sectionGroupId ? { section_group_id: sectionGroupId } : {}),
            },
            { timeout: MARKUP_REQUEST_TIMEOUT_MS },
        );
        return response.data;
    },

    /**
     * Publish the caller's draft (owner-or-admin); promotes it to a shared snapshot.
     * With `shapeIds`, only those draft shapes become the snapshot and the draft stays
     * live (per-markup publish); repeating with a different id set replaces the
     * snapshot, which is also how a single markup is unpublished.
     */
    publish: async (
        markupId: string,
        expectedVersion: number | null,
        shapeIds?: string[],
    ): Promise<ApiMarkupSet> => {
        if (setMarkupLayer(markupId)) {
            throw new Error(
                `An ${setPageWord}'s markup is saved with its item; it has no publish step`,
            );
        }
        try {
            const resp = await axios.post(
                `/api/ai/markup/${markupId}/publish`,
                { expected_version: expectedVersion, shape_ids: shapeIds },
                { timeout: MARKUP_REQUEST_TIMEOUT_MS },
            );
            return resp.data;
        } catch (err) {
            if (isAxiosError(err) && err.response?.status === 409) {
                throw new MarkupVersionConflict(currentVersionFrom(err.response.data?.detail));
            }
            throw err;
        }
    },

    /**
     * Clear a file whose markup is one layer of its own (an attachment set page) in one request,
     * fenced by the layer version this tab last saw: true once cleared, false for any other file,
     * which clears through `remove` and `purgeHistory`. A layer changed since this tab read it
     * rejects with {@link MarkupVersionConflict}.
     */
    clearLayer: async (fileId: string): Promise<boolean> => {
        const layer = setMarkupLayer(fileId);
        if (!layer) return false;
        await clearSetLayer(layer);
        return true;
    },

    /** Soft-delete a markup set the caller owns (owner-or-admin). */
    remove: async (markupId: string): Promise<void> => {
        const layer = setMarkupLayer(markupId);
        if (layer) return clearSetLayer(layer);
        await axios.delete(`/api/ai/markup/${markupId}`, { timeout: MARKUP_REQUEST_TIMEOUT_MS });
    },

    /**
     * Hard-delete the caller's ENTIRE published trail for this scope (author-only).
     * Backs "Clear sheet" — superseded snapshots must not resurface in the history.
     */
    purgeHistory: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        sectionGroupId?: string | null,
    ): Promise<void> => {
        // A set page keeps no published trail: Clear is deleting its one layer.
        const layer = setMarkupLayer(fileId);
        if (layer) return clearSetLayer(layer);
        await axios.delete(`/api/ai/markup/${fileId}/${page}/history`, {
            params: {
                ...(revisionId ? { revision_id: revisionId } : {}),
                ...(sectionGroupId ? { section_group_id: sectionGroupId } : {}),
            },
            timeout: MARKUP_REQUEST_TIMEOUT_MS,
        });
    },

    /** Store one validated file-scoped binary sidecar. The digest in the URL
     * makes retries idempotent; the server independently verifies bytes/MIME/hash. */
    putImage: async (
        fileId: string,
        attachment: MarkupImageAttachment,
        blob: Blob,
    ): Promise<void> => {
        rejectSetFile(fileId, "Image markups");
        const resp = await axios.put<ApiMarkupImage>(
            `/api/ai/markup/images/${fileId}/${attachment.imageRef}`,
            blob,
            { headers: { "Content-Type": attachment.mime } },
        );
        if (
            resp.data.imageRef !== attachment.imageRef ||
            resp.data.mime !== attachment.mime ||
            resp.data.size !== blob.size
        ) {
            throw new Error("Markup image upload response failed integrity validation");
        }
    },

    /** Fetch one sidecar through the authenticated tenant/file boundary. */
    getImage: async (fileId: string, imageRef: string): Promise<Blob> => {
        rejectSetFile(fileId, "Image markups");
        const resp = await axios.get<Blob>(`/api/ai/markup/images/${fileId}/${imageRef}`, {
            responseType: "blob",
        });
        return resp.data;
    },

    /**
     * Wrap a client-rendered page bitmap in a single-page PDF with the given markup
     * sets written as native PDF annotations (editable in Bluebeam, attributed via
     * each set's `author`). The page is sized 1 pt == 1 image pixel server-side, so
     * normalized shape coords and calibration math carry over unchanged.
     */
    annotateImage: async (args: {
        image: Blob;
        fileName: string;
        pageW: number;
        pageH: number;
        sets: MarkupExportSet[];
    }): Promise<Blob> => {
        const form = new FormData();
        form.append("image", args.image, "page.jpg");
        form.append(
            "payload",
            JSON.stringify({
                file_name: args.fileName,
                page_w: args.pageW,
                page_h: args.pageH,
                sets: args.sets,
            }),
        );
        const resp = await axios.post("/api/ai/markup/annotate-image", form, {
            responseType: "blob",
        });
        return resp.data;
    },

    /**
     * Build the overlay tool's layered PDF: each sheet its own PDF layer (vector,
     * tinted and placed as on screen), the markup sets as native annotations on
     * their own layers. `sheets` are in stack order with the base first; the base
     * takes no transform. The server reads each sheet's file after the checks a
     * download of it passes (Export included). The page is the base sheet in PDF
     * points, so markup coordinates and calibration carry over unchanged.
     */
    overlayPdf: async (args: {
        fileName: string;
        sheets: OverlayPdfSheet[];
        sets: MarkupExportSet[];
        /** The connection/grid overlays as a base64 PNG over the base page, if any show. */
        annotationsPng?: string | null;
    }): Promise<Blob> => {
        const resp = await axios.post(
            "/api/ai/markup/overlay-pdf",
            {
                file_name: args.fileName,
                sheets: args.sheets.map((sheet) => ({
                    file_id: sheet.fileId,
                    name: sheet.name,
                    tint: sheet.tint,
                    paper_luminance: sheet.paperLuminance,
                    opacity: sheet.opacity,
                    visible: sheet.visible,
                    transform: sheet.transform,
                    clip: sheet.clip,
                })),
                sets: args.sets,
                annotations_png: args.annotationsPng ?? null,
            },
            { responseType: "blob" },
        );
        return resp.data;
    },

    /**
     * Fetch the page's PDF with the selected markup layers written as native PDF
     * annotations: published sets (everyone's) and/or the caller's own draft. When
     * nothing matches the selection, the server returns the original PDF unchanged,
     * so downloads can always route through here. `allPages` exports every page's
     * markup burned onto its own page — the whole-book download for multi-page
     * documents (specs); single-page sheets omit it.
     */
    download: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        options: MarkupDownloadOptions,
        allPages = false,
        sectionGroupId?: string | null,
    ): Promise<Blob> => {
        rejectSetFile(fileId, "A markup export");
        const resp = await axios.get(`/api/ai/markup/${fileId}/${page}/download`, {
            params: {
                ...(revisionId ? { revision_id: revisionId } : {}),
                ...(sectionGroupId ? { section_group_id: sectionGroupId } : {}),
                include_published: options.includePublished,
                include_draft: options.includeUnpublished,
                ...(allPages ? { all_pages: true } : {}),
            },
            responseType: "blob",
        });
        return resp.data;
    },

    /**
     * Fetch only a spec section's own pages from the book its section group points into,
     * with the selected markup layers burned in (the same layer rules as `download`). Always
     * a server cut, even with no layers: the book itself is `download`'s job. Rejects with
     * {@link SectionPagesUnknown} when the server can't place the section's pages, and with a
     * plain error when a success carries no page range (see below). A transient failure is sent
     * again within `withTransientRetry`'s budget; a refusal never is.
     */
    downloadSection: async (
        fileId: string,
        page: number,
        revisionId: string | null,
        options: MarkupDownloadOptions,
        sectionGroupId: string,
    ): Promise<SectionDownload> => {
        rejectSetFile(fileId, "A markup export");
        let resp;
        try {
            resp = await withTransientRetry(() =>
                axios.get<Blob>(`/api/ai/markup/${fileId}/${page}/download`, {
                    params: {
                        ...(revisionId ? { revision_id: revisionId } : {}),
                        section_group_id: sectionGroupId,
                        page_scope: "section",
                        include_published: options.includePublished,
                        include_draft: options.includeUnpublished,
                    },
                    responseType: "blob",
                }),
            );
        } catch (err) {
            throw await sectionDownloadError(err);
        }
        const start = pageHeader(resp.headers, "X-Section-Page-Start");
        const end = pageHeader(resp.headers, "X-Section-Page-End");
        const sourcePageCount = pageHeader(resp.headers, "X-Source-Page-Count");
        // The page headers are what make a success a section cut. An ai_engine without the
        // cut ignores `page_scope` and answers 200 with the whole book, as does a path that
        // strips the headers: refuse it rather than save the book under the section's name.
        if (
            start === undefined ||
            end === undefined ||
            sourcePageCount === undefined ||
            start < 1 ||
            end < start ||
            end > sourcePageCount
        ) {
            throw new Error("Section download returned no page range; not a section cut");
        }
        return {
            blob: resp.data,
            fileName: contentDispositionFileName(
                responseHeader(resp.headers, "Content-Disposition"),
            ),
            pages: { start, end, sourcePageCount },
        };
    },
};
